// Hideo - what a drop from outside does, with the stores: a new video on the plan pages (dropNewVideo), a new source or
// hook on their pages, a text file in the script editor. The rules are src/features/drop.ts; installDrops() is called
// once by the shell (App.vue) and adds the listeners of the Sources and Hooks pages (on the document) and of the script
// editor (on #scriptBody / #scriptMd).
import { t } from '../i18n'
import { cleanHtml } from '../features/editor/html'
import { mdToHtml } from '../features/editor/markdown'
import { fountainToHtml } from '../features/editor/fountainHtml'
import { MAX_AUDIO, dropFile, dropText, dropZone, droppedHook, droppedScript, droppedTextInto, droppedVideoBase, fileTitle, isFountainFile, isTextFile, type DropVideoOpts } from '../features/drop'
import { formatOfFilter } from '../features/formats'
import { droppedSource } from '../features/sources'
import { droppedSaved } from '../features/savedVideos'
import { uiAlert } from './notices'
import { useDataStore } from './data'
import { useDialogsStore } from './dialogs'
import { usePedStore } from './ped'
import { newItemProject } from './projects'
import { useVideoStore } from './video'
import { useSavedStore } from './saved'
import { saveAppData } from '../features/storage/save'
import { mediaPut } from '../features/cloud/media'
import { hasFiles, readImg } from '../utils/image'
import { useEditorStore } from './editor'

/** New video from a file or text dropped from outside on the Board, the Calendar or the Timeline (opts: status, dates),
 *  saved at once; then its dialog opens. Nothing happens without a file or text */
export async function dropNewVideo(e: DragEvent, opts: DropVideoOpts = {}): Promise<void> {
  const f = dropFile(e), text = f ? '' : dropText(e), d = useDataStore().getData()
  const pid = newItemProject(), v = droppedVideoBase(d, pid, opts)
  v.seriesId = formatOfFilter(d, usePedStore().filter.series, pid)   // format of the active filter, if the project has it
  if (f) {
    v.title = fileTitle(f)
    if (f.type.startsWith('image/')) v.thumbnail = await mediaPut(await readImg(f, 1280), 'img')
    else if (f.type.startsWith('audio/')) {
      if (f.size > MAX_AUDIO) { uiAlert(t('Audio file too large (maximum 10 MB).')); return }
      v.voiceNote = await mediaPut(f, 'voice')
    } else if (isTextFile(f)) await droppedScript(d, v, f)
    else { uiAlert(t('Here you can drop an image, an audio, a text file (.txt, .md) or a sentence.')); return }
  } else if (text) droppedTextInto(v, text)
  else return
  d.videos.push(v); saveAppData(d)
  useVideoStore().open(v.id)
}

/** Script editor: a .txt / .md / .fountain file dropped on the text is inserted where it is dropped (at the end when the drop
 *  point is not inside the script; a .fountain one as a screenplay turned into the page); in Markdown mode at the cursor
 *  (in Fountain mode: FountainEditor.vue). Text dragged inside the editor keeps the browser's behaviour */
export function installScriptDrop(body: HTMLElement | null, md: HTMLTextAreaElement | null): void {
  if (!body || !md) return
  const onDrop = async (e: DragEvent) => {
    const f = dropFile(e)
    if (!f) return
    e.preventDefault()
    if (!isTextFile(f)) { uiAlert(t('In the script you can drop a text file (.txt or .md).')); return }
    const text = await f.text()
    if (useEditorStore().mode === 'md') {
      const p = md.selectionStart || md.value.length
      md.value = md.value.slice(0, p) + text + md.value.slice(p)
    } else {
      const doc = document as Document & { caretRangeFromPoint?: (x: number, y: number) => Range | null }
      let r = doc.caretRangeFromPoint ? doc.caretRangeFromPoint(e.clientX, e.clientY) : null
      if (!r || !body.contains(r.startContainer)) { r = document.createRange(); r.selectNodeContents(body); r.collapse(false) }
      body.focus()
      const s = getSelection()!
      s.removeAllRanges(); s.addRange(r)
      document.execCommand('insertHTML', false, cleanHtml(isFountainFile(f) ? fountainToHtml(text) : mdToHtml(text)))
    }
    useEditorStore().queueSave()
  }
  for (const el of [body, md]) {
    el.addEventListener('dragover', e => { if (hasFiles(e)) e.preventDefault() })
    el.addEventListener('drop', onDrop)
  }
}

/** Sources: a link → the source dialog with that link (a link fills link and title, its site; other text the title).
 *  Saved videos: a link → the saved video dialog with that link (other text is ignored).
 *  Hooks: some text → the hook dialog with title and content (the title selected) */
export function installPageDrops(doc: Pick<Document, 'addEventListener'> = document): void {
  const dialogs = useDialogsStore()
  dropZone(doc, 'view-sources', txt => dialogs.show('source', { id: null, prefill: droppedSource(txt) }))
  dropZone(doc, 'view-saved', txt => { const p = droppedSaved(txt); if (p) dialogs.show('saved', { ...useSavedStore().newArgs(), prefill: p }) })
  dropZone(doc, 'view-vault', txt => dialogs.show('hook', { id: null, prefill: droppedHook(txt) }))
}

let installed = false
/** The shell's drop listeners (App.vue, once): script editor first, then the Sources and Hooks pages, like before */
export function installDrops(): void {
  if (installed) return
  installed = true
  installScriptDrop(document.getElementById('scriptBody'), document.getElementById('scriptMd') as HTMLTextAreaElement | null)
  installPageDrops(document)
}
